/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

import useMediaQuery from '../../hooks/useMediaQuery';
import useScroll from '../../hooks/useScrollHeader';
import { theme } from '../../hooks/useTheme/theme';
import quickLinks from '../../mocks/data/quicklink';
import { aboutClubRef } from '../ClubDetails';
import { contactClubRef } from '../ClubDetailsContactDetails';
import {
  clubCasualRef,
  disabilityRef,
  faProgRef,
  waysToPlayRef,
} from '../HomePage';
import { DivLine, QuickDots, QuickLine, RedLine } from '../Icons';
import { aboutTeamRef } from '../TeamInfo';
import { teamStatsRef } from '../TeamStatus';
import { teamClubRef } from '../VenueClubList';
import useStyles from './styles';

const QuickLinks = ({ statsHidden = false }) => {
  const styles = useStyles();
  const [divAnimation, setDivAnimation] = useState(true);
  const [isHovered, setIsHovered] = useState(true);
  const { scrollDirection } = useScroll();
  const location = useLocation();
  const isDesktop = useMediaQuery('screen and (min-width: 1196px)');
  const isIpad = useMediaQuery(
    'screen and (min-width: 810px) and (max-width: 1196px)'
  );
  const isMobile = useMediaQuery(
    'screen and (min-width: 760px) and (max-width: 810px)'
  );
  const isSmallMobile = useMediaQuery('screen and (max-width: 760px)');
  const MQtoHideQuickLinks = useMediaQuery('screen and (max-width: 1276px)');

  const position = window.pageYOffset || document.documentElement.scrollTop;
  const currentPage =
    location.pathname === '/'
      ? 'homepage'
      : location.pathname.startsWith('/club')
      ? 'clubpage'
      : location.pathname.startsWith('/team')
      ? 'teampage'
      : 'other';

  const getQuickLinks = page => {
    switch (page) {
      case 'homepage':
        return quickLinks.HomePage;
      case 'clubpage':
        return quickLinks.ClubPage;
      case 'teampage':
        return quickLinks.TeamPage;
      default:
        return null;
    }
  };

  const currentQuickLinks = getQuickLinks(currentPage);

  const updateAnimationForClubOrTeam = useCallback(
    position => {
      const threshold = isDesktop ? 360 : 636;
      if (position > threshold) {
        setDivAnimation(false);
      } else if (position < threshold) {
        setDivAnimation(true);
      }
    },
    [isDesktop],
  );

  const updateAnimationForHome = useCallback(
    position => {
      const threshold = isDesktop ? 370 : 636;
      if (position > threshold) {
        setDivAnimation(false);
      } else if (position < threshold) {
        setDivAnimation(true);
      }
    },
    [isDesktop],
  );

  useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset || document.documentElement.scrollTop;
      // For ClubPage and TeamPage
      if (currentPage === 'clubpage' || currentPage === 'teampage') {
        updateAnimationForClubOrTeam(position);
      }
      // For HomePage
      if (currentPage === 'homepage') {
        updateAnimationForHome(position);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isDesktop, divAnimation, currentPage, updateAnimationForClubOrTeam, updateAnimationForHome]);

  useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset || document.documentElement.scrollTop;
      // For ClubPage and TeamPage
      if (currentPage === 'clubpage' || currentPage === 'teampage') {
        if (isIpad ? position > 290 : position > 310) {
          setDivAnimation(false);
        }
        if (isIpad ? position < 290 : position < 310) {
          setDivAnimation(true);
        }
      }
      // For Homepage
      if (currentPage === 'homepage') {
        if (isIpad ? position > 360 : position > 540) {
          setDivAnimation(false);
        }
        if (isIpad ? position < 360 : position < 540) {
          setDivAnimation(true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isIpad, divAnimation, currentPage]);

  const handleClick = () => {
    if (position > 370 && !divAnimation) {
      setDivAnimation(true);
    } else if (position < 370 && divAnimation) {
      setDivAnimation(false);
    } else if (position > 370 && divAnimation) {
      setDivAnimation(false);
    } else {
      setDivAnimation(true);
    }
  };

  function handleHover(isHovered, divAnimation) {
    let quickLine = document.querySelector('[class="quickLine"]'),
      quickDots = document.querySelector('[class="quickDots"]');

    (() => {
      if (divAnimation) {
        if (isHovered) {
          quickLine.setAttribute('stroke', theme.colors.blue);
        } else {
          quickLine.setAttribute('stroke', theme.colors.red);
        }
      }
    })();

    (() => {
      if (!divAnimation) {
        if (isHovered) {
          quickDots.setAttribute('fill', theme.colors.blue);
          quickDots.setAttribute('stroke', theme.colors.blue);
        } else {
          quickDots.setAttribute('fill', theme.colors.red);
          quickDots.setAttribute('stroke', theme.colors.red);
        }
      }
    })();
  }

  function handleQuickLinkClick(index) {
    const findIndex =
      index === 0
        ? waysToPlayRef.current
        : index === 1
        ? clubCasualRef.current
        : index === 2
        ? faProgRef.current
        : index === 3
        ? disabilityRef.current
        : null;
    const findClubIndex =
      index === 0
        ? aboutClubRef.current
        : index === 1
        ? teamClubRef.current
        : index === 2
        ? contactClubRef.current
        : null;
    const findTeamIndex =
      index === 0
        ? aboutTeamRef.current
        : index === 1
        ? teamStatsRef.current
        : index === 2
        ? contactClubRef.current
        : null;
    if (findIndex) {
      return (
        findIndex.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        }),
        setDivAnimation(false)
      );
    }
    if (findClubIndex) {
      return (
        findClubIndex.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        }),
        setDivAnimation(false)
      );
    }
    if (findTeamIndex) {
      return (
        findTeamIndex.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        }),
        setDivAnimation(false)
      );
    }
  }

  return (
    <div
      css={styles.quickLinks(
        currentPage,
        divAnimation,
        scrollDirection,
        window.pageYOffset || document.documentElement.scrollTop,
        isDesktop,
        isIpad,
        isMobile,
        isSmallMobile,
        MQtoHideQuickLinks
      )}
    >
      <div
        role="button"
        css={styles.onThisPage(divAnimation)}
        onClick={handleClick}
        onKeyDown={e => e.key === 'Enter' && handleClick()}
        id={`quick-link-${currentPage}`}
        tabIndex={0}
        onMouseEnter={() => {
          setIsHovered(false);
          handleHover(isHovered, divAnimation);
        }}
        onMouseLeave={() => {
          setIsHovered(true);
          handleHover(isHovered, divAnimation);
        }}
      >
        <p css={styles.pageHeading}>On this page</p>
        <div css={styles.closeIcon(divAnimation)}>
          {divAnimation ? <QuickLine /> : <QuickDots />}
        </div>
        <DivLine />
      </div>
      <div
        css={styles.quick(divAnimation, currentPage, position, scrollDirection)}
      >
        <div css={styles.quickAnimate}>
          <ul css={styles.quickLinkList}>
            {getQuickLinks(currentPage).map((data, index) => {
              const isLastIndex = index === currentQuickLinks.length - 1;
              return (
                <li
                  key={index}
                  css={styles.listData(statsHidden)}
                  id={data.replaceAll(' ', '-').toLowerCase()}
                >
                  <RedLine key={index} css={styles.svgStyle} />
                  <div
                    role="link"
                    tabIndex={0}
                    onClick={() => handleQuickLinkClick(index)}
                    onKeyDown={e =>
                      e.key === 'Enter' && handleQuickLinkClick(index)
                    }
                    css={styles.quickLink}
                  >
                    {data}
                  </div>
                  {!isLastIndex && <DivLine />}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QuickLinks;
